import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import classNames from 'classnames'

import { Drawer } from '@/components/drawer/Drawer'
import { Pill } from '@/components/pills/Pill'
import { BaltoGPTChat } from '@/components/baltoGPT/BaltoGPTChat'
import { AudioPlayerProvider } from '@/components/media/AudioPlayerContext'
import { VideoPlayerProvider } from '@/components/media/VideoPlayerContext'
import { TranscriptProvider } from '@/components/transcript/TranscriptContext'
import {
  fetchInsightCoachingConflicts,
  launchInsightExperiment,
  markInsightReviewed,
} from '@/reducers/insights/agenticInsights.actions'
import { searchInsightWithBaltoGPT, resetChatFeed } from '@/reducers/baltoGPT/baltoGPT.actions'

import { Spine } from './Spine'
import { ConversionPanel } from './ConversionPanel'
import { DeliveryOptions } from './DeliveryOptions'
import { EvidenceCard } from './EvidenceCard'
import { Sparkline } from './Sparkline'
import { ExperimentScope } from './ExperimentScope'
import { DRAWER_KIND, SCOPE_LOADING, SPINE_LEAD } from './AgenticInsights.constants'
import {
  CHANNEL_CALL,
  convertedCountAtRate,
  formatLiftPoints,
  formatMonthlyCount,
  formatRatePercent,
  getBaselineConversionRate,
  getChannelFilterNoun,
  getExpectedConversionsPerWeek,
  getExperimentPanelProps,
  getMeasuredCallsPhrase,
  getMeasuredLiftPoints,
  getMonthlyProjectionFactor,
  getOpportunityLabel,
  getProjectedConversionRate,
  isCompletedExperiment,
  isLaunched,
  isRunningExperiment,
  splitRecommendationIntoDisplayItems,
} from './AgenticInsights.helpers'

const CHAT_SUGGESTIONS = [
  { text: 'Which agents lose the most to this?' },
  { text: 'Show me a call where this happened' },
]

const InsightChat = ({ insight }) => {
  const dispatch = useDispatch()
  const callIds = insight.evidence.map((item) => item.callId)

  return (
    <div className="insight-drawer__chat">
      <div className="insight-drawer__section-head">Ask about this insight</div>
      <BaltoGPTChat
        useMinimalUi
        channel={insight.channel}
        callIds={callIds}
        suggestions={CHAT_SUGGESTIONS}
        disableAutoScroll
        disableAutoFocus
        onSubmit={(values, signal) =>
          dispatch(searchInsightWithBaltoGPT(insight.id, values, signal))
        }
      />
    </div>
  )
}

const InsightBody = ({ insight, playbooks }) => {
  const dispatch = useDispatch()
  const { launchingInsightId } = useSelector((state) => state.agenticInsights)
  const launched = isLaunched(insight)
  // Both deliveries need a live phone call: the prompt appears during one, and
  // coaching is published to one afterwards. So an email or chat insight has
  // nothing to launch. See TREN-421 for whether that should ever change.
  const voice = insight.channel === CHANNEL_CALL
  const running = isRunningExperiment(insight)
  const completed = isCompletedExperiment(insight)
  // The pre-launch projection, for an insight nobody has launched yet. Once one
  // is running these are the wrong numbers to show — conversionPctLift is frozen
  // at generation time and never reflects the experiment.
  const baselineRate = getBaselineConversionRate(insight.opportunityStats)
  const projectedRate = getProjectedConversionRate(
    insight.opportunityStats,
    insight.conversionPctLift
  )
  // What the experiment actually measured.
  const liftPoints = getMeasuredLiftPoints(insight)
  const measuredBaseline = formatRatePercent(insight.experiment?.baselineConversionRate)
  const measuredRate = formatRatePercent(insight.experiment?.conversionRate)
  const perWeek = getExpectedConversionsPerWeek(insight)
  const monthlyFactor = getMonthlyProjectionFactor(insight)
  const conclusive = insight.opportunityStats?.conclusiveCalls ?? 0
  const lostToReason = Math.round((insight.pctOfConclusive ?? 0) * conclusive)
  const launching = launchingInsightId === insight.id
  // Three states, not two: SCOPE_LOADING until the request settles, then either the
  // scope or null if it failed. Launch has to wait for it — it carries the
  // playbooks the guidance attaches to — but a failed request must not block
  // launching forever, so the two cases can't share a value.
  const [scope, setScope] = useState(SCOPE_LOADING)

  // Where this experiment's guidance and coaching go — and, before launch, whether
  // a running experiment is holding its agents. Fetched per drawer rather than
  // carried on the insight, because working it out joins the call table per
  // insight, which is the cost TREN-400 took out of the list endpoint. Needed
  // after launch too, so the panel can say where it actually went.
  useEffect(() => {
    let stale = false
    // Reset first: this component isn't keyed on the insight, so moving between
    // insights without closing the drawer would otherwise keep showing the
    // previous one's answer until the new one arrives.
    setScope(SCOPE_LOADING)
    if (voice) {
      dispatch(fetchInsightCoachingConflicts(insight.id, insight.organizationId)).then((loaded) => {
        if (!stale) {
          setScope(loaded)
        }
      })
    }
    return () => {
      stale = true
    }
  }, [dispatch, insight.id, insight.organizationId, voice])

  const handleLaunch = ({ realtime, coaching }) => {
    dispatch(
      launchInsightExperiment(insight, {
        realtime,
        coaching,
        // The derived playbooks the prompt attaches to. If the scope request
        // failed we fall back to the run's configured playbooks — what the launch
        // used before this existed, and right for all but the handful of runs that
        // aren't playbook-scoped.
        playbookCids: scope?.guidancePlaybookCids ?? insight.playbookCids,
      })
    )
  }

  const statusPill = (() => {
    if (completed) {
      return <Pill informative>Completed</Pill>
    }
    if (running) {
      return (
        <Pill
          success
        >{`Running · day ${insight.experiment.daysElapsed ?? 0} of ${insight.experiment.daysToTrack ?? 30}`}</Pill>
      )
    }
    return <Pill brand>Not launched yet</Pill>
  })()

  return (
    <>
      <div className="insight-drawer__top">
        {statusPill}
        {launched && insight.experiment.startedByUser && (
          <span className="insight-drawer__startedby">
            Launched by {insight.experiment.startedByUser}
          </span>
        )}
      </div>
      <div className="insight-drawer__hero">
        <Spine
          nodes={[
            { kind: 'goal', lead: SPINE_LEAD.GOAL, value: getOpportunityLabel(insight) },
            {
              kind: 'obstacle',
              lead: SPINE_LEAD.OBSTACLE,
              value: insight.conversionReason || insight.title,
            },
            {
              kind: 'fix',
              lead: SPINE_LEAD.FIX,
              value: insight.recommendation,
              items: splitRecommendationIntoDisplayItems(insight.recommendation),
            },
          ]}
        />
        {launched ? (
          <ConversionPanel
            boxed
            heading={running ? 'Conversion rate · so far' : 'Conversion rate · measured'}
            {...getExperimentPanelProps(insight)}
          />
        ) : (
          <ConversionPanel
            boxed
            nowKey="Now"
            nowValue={formatRatePercent(baselineRate) || '—'}
            nowSub={formatMonthlyCount(
              insight.opportunityStats?.convertedCalls ?? null,
              monthlyFactor
            )}
            projKey="Projected"
            projValue={formatRatePercent(projectedRate) || '—'}
            projSub={formatMonthlyCount(
              convertedCountAtRate(insight.opportunityStats, projectedRate),
              monthlyFactor
            )}
          />
        )}
      </div>

      {!launched && lostToReason > 0 && (
        <div className="insight-drawer__section">
          <div className="insight-drawer__section-head">What we found</div>
          <p className="insight-drawer__ground">
            About <b>~{lostToReason.toLocaleString()}</b> of your{' '}
            <b>{getOpportunityLabel(insight)}</b> calls run into this objection, and most don&apos;t
            convert. From what works on similar calls, we think this recommendation could recover
            about <b>~{perWeek}</b> conversions a week.
          </p>
        </div>
      )}

      {!isEmpty(insight.evidence) && (
        <div className="insight-drawer__section">
          <div className="insight-drawer__section-head">Evidence</div>
          <p className="insight-drawer__section-sub">
            Real calls that didn&apos;t convert because this objection came up.
          </p>
          <div className="insight-drawer__evidence">
            {insight.evidence.slice(0, 3).map((item) => (
              <EvidenceCard key={item.id} evidence={item} />
            ))}
          </div>
          {insight.evidence.length > 3 && (
            <p className="insight-drawer__evmore">
              See {insight.evidence.length - 3} more calls like this →
            </p>
          )}
        </div>
      )}

      {(running || completed) && (
        <div className="insight-drawer__section">
          <div className="insight-drawer__section-head">Results</div>
          <div className="insight-drawer__results">
            <div className="insight-drawer__results-lift">
              <div
                className={classNames('insight-drawer__results-value', {
                  // Already whole points, so this is the same value the label
                  // prints — the colour cannot disagree with the number.
                  'insight-drawer__results-value--negative': liftPoints < 0,
                })}
              >
                {formatLiftPoints(liftPoints) || '—'}
              </div>
              <div className="insight-drawer__results-label">
                {completed ? 'measured lift' : 'lift so far'}
              </div>
              {measuredRate && measuredBaseline && (
                <div className="insight-drawer__results-baseline">
                  {measuredRate} vs {measuredBaseline} baseline
                </div>
              )}
            </div>
            {isEmpty(insight.experiment.dailyTrend) ? (
              // An experiment on its first day has nothing to plot yet. Saying so
              // beats an empty value beside a flat line, which reads as broken.
              <div className="insight-drawer__results-waiting">No calls yet</div>
            ) : (
              <Sparkline
                data={insight.experiment.dailyTrend}
                width={200}
                height={40}
                className="sparkline--measured"
                fill
              />
            )}
          </div>
          <p className="insight-drawer__caveat">
            Conversion rate for {getMeasuredCallsPhrase(insight)}. Compared against the conversion
            rate from before you launched the experiment.
          </p>
        </div>
      )}

      {!launched && !voice && (
        <div className="insight-drawer__section">
          <div className="insight-drawer__section-head">Launch as an experiment</div>
          <p className="agentic-muted">
            Experiments aren&apos;t available for {getChannelFilterNoun(insight.channel)} insights.
            Both ways we deliver a recommendation — a prompt during the call, and coaching
            afterwards — only work on phone calls.
          </p>
        </div>
      )}

      {!launched && voice && (
        <div className="insight-drawer__section">
          <div className="insight-drawer__section-head">Launch as an experiment</div>
          <DeliveryOptions
            insight={insight}
            launching={launching}
            scope={scope}
            playbooks={playbooks}
            onLaunch={handleLaunch}
          />
        </div>
      )}

      {launched && (
        <div className="insight-drawer__section">
          <div className="insight-drawer__section-head">What&apos;s live for agents</div>
          <ExperimentScope
            launched
            insight={insight}
            playbooks={playbooks}
            playbookCids={scope?.guidancePlaybookCids ?? []}
            coachedAgents={scope?.agents ?? []}
          />
        </div>
      )}

      <InsightChat insight={insight} />
    </>
  )
}

const ReasonBody = ({ insight }) => {
  const conclusive = insight.opportunityStats?.conclusiveCalls ?? 0
  const lost = Math.round((insight.pctOfConclusive ?? 0) * conclusive)
  const pct = Math.round((insight.pctOfConclusive ?? 0) * 100)

  return (
    <>
      <div className="insight-drawer__top">
        <Pill informative>Reason</Pill>
      </div>
      <div className="insight-drawer__hero">
        <Spine
          nodes={[
            { kind: 'goal', lead: SPINE_LEAD.GOAL, value: getOpportunityLabel(insight) },
            {
              kind: 'obstacle',
              lead: SPINE_LEAD.OBSTACLE,
              value: insight.conversionReason || insight.title,
            },
          ]}
        />
        <div className="insight-drawer__howoften">
          <div className="insight-drawer__section-head">How often this happens</div>
          <p>
            <b>{pct}%</b> of decided calls, about <b>{lost.toLocaleString()}</b> a month.
          </p>
        </div>
      </div>

      {!isEmpty(insight.evidence) && (
        <div className="insight-drawer__section">
          <div className="insight-drawer__section-head">Evidence</div>
          <p className="insight-drawer__section-sub">
            Real calls that didn&apos;t convert for this reason.
          </p>
          <div className="insight-drawer__evidence">
            {insight.evidence.slice(0, 3).map((item) => (
              <EvidenceCard key={item.id} evidence={item} />
            ))}
          </div>
          {insight.evidence.length > 3 && (
            <p className="insight-drawer__evmore">
              See {insight.evidence.length - 3} more calls like this →
            </p>
          )}
        </div>
      )}

      <div className="insight-drawer__section">
        <div className="insight-drawer__section-head">Why there&apos;s no recommendation</div>
        <div className="insight-drawer__callout">
          This happens off the call — a process gap, not something an agent can change while talking
          to a customer. We surface it so your team can look into it.
        </div>
      </div>

      <InsightChat insight={insight} />
    </>
  )
}

// Executive (operational) insight: an off-call fix for the team. Same shape as
// the reason drawer, but it DOES carry a recommendation (aimed at the team, not
// an on-call agent line), and there's no launch flow.
const ExecutiveBody = ({ insight }) => {
  const conclusive = insight.opportunityStats?.conclusiveCalls ?? 0
  const lost = Math.round((insight.pctOfConclusive ?? 0) * conclusive)
  const pct = Math.round((insight.pctOfConclusive ?? 0) * 100)

  return (
    <>
      <div className="insight-drawer__top">
        <Pill informative>Executive</Pill>
      </div>
      <div className="insight-drawer__hero">
        <Spine
          nodes={[
            { kind: 'goal', lead: SPINE_LEAD.GOAL, value: getOpportunityLabel(insight) },
            {
              kind: 'obstacle',
              lead: SPINE_LEAD.OBSTACLE,
              value: insight.conversionReason || insight.title,
            },
            {
              kind: 'fix',
              lead: SPINE_LEAD.FIX_EXEC,
              value: insight.recommendationSummary || insight.recommendation,
            },
          ]}
        />
        <div className="insight-drawer__howoften">
          <div className="insight-drawer__section-head">What we found</div>
          <p>
            About <b>~{lost.toLocaleString()}</b> of your <b>{getOpportunityLabel(insight)}</b>{' '}
            calls run into this a month — that&apos;s <b>{pct}%</b> of the calls where a decision
            was reached.
          </p>
        </div>
      </div>

      {!isEmpty(insight.evidence) && (
        <div className="insight-drawer__section">
          <div className="insight-drawer__section-head">Evidence</div>
          <p className="insight-drawer__section-sub">Real calls that ran into this.</p>
          <div className="insight-drawer__evidence">
            {insight.evidence.slice(0, 3).map((item) => (
              <EvidenceCard key={item.id} evidence={item} />
            ))}
          </div>
          {insight.evidence.length > 3 && (
            <p className="insight-drawer__evmore">
              See {insight.evidence.length - 3} more calls like this →
            </p>
          )}
        </div>
      )}

      <div className="insight-drawer__section">
        <div className="insight-drawer__section-head">Why this isn&apos;t an agent action</div>
        <div className="insight-drawer__callout">
          This happens off the call — a process or training gap, not something an agent can change
          while talking to a customer. The recommendation above is for your team to act on.
        </div>
      </div>

      <InsightChat insight={insight} />
    </>
  )
}

const InconclusiveBody = ({ opportunity }) => (
  <>
    <div className="insight-drawer__top">
      <Pill informative>Inconclusive</Pill>
    </div>
    <div className="insight-drawer__eyebrow">Outcome unclear</div>
    <h3 className="insight-drawer__plain-title">Inconclusive calls</h3>
    <p className="insight-drawer__plain-def">
      Calls in “{opportunity.label || opportunity.name}” where Balto couldn&apos;t confidently tell
      whether the goal was reached — {Math.round(opportunity.inconclusivePct)}% of these calls.
    </p>
    <div className="insight-drawer__section">
      <div className="insight-drawer__section-head">Why this happens</div>
      <div className="insight-drawer__callout">
        Usually the call was too short, cut off, or ambiguous to score. Not misses — just
        undetermined.
      </div>
    </div>
  </>
)

const DRAWER_TITLE = {
  [DRAWER_KIND.INSIGHT]: 'Insight',
  [DRAWER_KIND.EXECUTIVE]: 'Executive insight',
  [DRAWER_KIND.REASON]: 'Reason',
  [DRAWER_KIND.INCONCLUSIVE]: 'Inconclusive calls',
}

// One drawer surface for every deep-dive: an insight (with launch), a reason
// (spine minus the recommendation, no launch), or the inconclusive slice of an
// opportunity. Conditionally mounted by the page so autoTrigger re-opens it
// cleanly on each new selection.
//
// Converted has no drawer: everything we know about that slice is the share and
// the count, both already on the card, so opening one only repeated them.
export const InsightDrawer = ({ drawer, insights, playbooks = [], onClose }) => {
  const dispatch = useDispatch()
  const insight =
    drawer.insightId != null ? insights.find((item) => item.id === drawer.insightId) : null

  useEffect(() => {
    dispatch(resetChatFeed())
    if ((drawer.kind === DRAWER_KIND.INSIGHT || drawer.kind === DRAWER_KIND.EXECUTIVE) && insight) {
      dispatch(markInsightReviewed(insight.id))
    }
    return () => dispatch(resetChatFeed())
  }, [drawer.insightId, drawer.kind])

  const renderBody = () => {
    if (drawer.kind === DRAWER_KIND.INSIGHT && insight) {
      return <InsightBody insight={insight} playbooks={playbooks} />
    }
    if (drawer.kind === DRAWER_KIND.EXECUTIVE && insight) {
      return <ExecutiveBody insight={insight} />
    }
    if (drawer.kind === DRAWER_KIND.REASON && insight) {
      return <ReasonBody insight={insight} />
    }
    if (drawer.kind === DRAWER_KIND.INCONCLUSIVE) {
      return <InconclusiveBody opportunity={drawer.opportunity} />
    }
    return null
  }

  return (
    <Drawer autoTrigger extraWide title={DRAWER_TITLE[drawer.kind]} onClose={onClose}>
      {() => (
        <VideoPlayerProvider>
          <AudioPlayerProvider>
            <TranscriptProvider transcript={[]} postcallTranscript={[]}>
              <div className="insight-drawer">{renderBody()}</div>
            </TranscriptProvider>
          </AudioPlayerProvider>
        </VideoPlayerProvider>
      )}
    </Drawer>
  )
}
